
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Lock, Unlock, Loader2, Pencil, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useSchool } from '../../../context/SchoolContext';
import StaffAccountAccessModal from './StaffAccountAccessModal';
import Toast from './Toast';
import { staffApi } from '../services/staffApi';
import { getStaffApiErrorMessage } from '../utils/staffErrors';

const EMPTY_PAGINATION = {
  count: 0,
  page: 1,
  page_size: 10,
  returned: 0,
  total_pages: 1,
  next: null,
  previous: null,
};

const normalizeStaffResponse = (response, requestedPage, pageSize) => {
  const root = response || {};
  const data = root?.data ?? root;

  let results = [];

  if (Array.isArray(data)) {
    results = data;
  } else if (Array.isArray(data?.results)) {
    results = data.results;
  } else if (Array.isArray(data?.data)) {
    results = data.data;
  }

  const metadata =
    root?.pagination ||
    data?.pagination ||
    data?.meta ||
    {};

  const count = Number(
    metadata.count ?? data?.count ?? results.length,
  );
  const normalizedPageSize = Number(
    metadata.page_size ?? data?.page_size ?? pageSize,
  );
  const currentPage = Number(
    metadata.page ?? data?.page ?? requestedPage,
  );
  const totalPages = Math.max(
    Number(
      metadata.total_pages ??
        data?.total_pages ??
        Math.ceil(count / Math.max(normalizedPageSize, 1)) ??
        1,
    ),
    1,
  );

  return {
    results,
    pagination: {
      count,
      page: currentPage,
      page_size: normalizedPageSize,
      returned: Number(metadata.returned ?? results.length),
      total_pages: totalPages,
      next: metadata.next ?? data?.next ?? null,
      previous: metadata.previous ?? data?.previous ?? null,
    },
  };
};

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const StaffTable = ({
  filters = {},
  page = 1,
  pageSize = 10,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const { schoolId } = useSchool();


  const [staffData, setStaffData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    ...EMPTY_PAGINATION,
    page_size: pageSize,
  });
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [toast, setToast] = useState(null);

  const fetchStaff = useCallback(async () => {
    if (!schoolId) return;

    setLoading(true);

    try {
      const response = await staffApi.getStaff(schoolId, {
        page,
        page_size: pageSize,
        q: filters.q,
        staff_type: filters.staff_type,
        department: filters.department,
        status: filters.status,
      });

      const normalized = normalizeStaffResponse(response, page, pageSize);

      setStaffData(normalized.results);
      setPagination(normalized.pagination);

      if (
        normalized.pagination.total_pages > 0 &&
        page > normalized.pagination.total_pages &&
        onPageChange
      ) {
        onPageChange(normalized.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch staff.', error);
      setStaffData([]);
      setPagination({
        ...EMPTY_PAGINATION,
        page,
        page_size: pageSize,
      });
      setToast({
        type: 'error',
        message: getStaffApiErrorMessage(
          error,
          'Unable to load staff members.',
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [
    schoolId,
    page,
    pageSize,
    filters.q,
    filters.staff_type,
    filters.department,
    filters.status,
    onPageChange,
    refreshVersion,
  ]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const pageNumbers = useMemo(() => {
    const total = pagination.total_pages;
    const current = pagination.page;

    return Array.from({ length: total }, (_, index) => index + 1).filter(
      (pageNumber) =>
        pageNumber === 1 ||
        pageNumber === total ||
        Math.abs(pageNumber - current) <= 1,
    );
  }, [pagination.page, pagination.total_pages]);

  const goToPage = (nextPage) => {
    const safePage = Math.min(
      Math.max(Number(nextPage), 1),
      pagination.total_pages,
    );

    if (safePage !== pagination.page && onPageChange) {
      onPageChange(safePage);
    }
  };

  const startNumber =
    pagination.count === 0
      ? 0
      : (pagination.page - 1) * pagination.page_size + 1;

  const endNumber = Math.min(
    (pagination.page - 1) * pagination.page_size + pagination.returned,
    pagination.count,
  );

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px] text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-4 text-left">Photo</th>
                <th className="px-4 py-4 text-left">Employee ID</th>
                <th className="px-4 py-4 text-left">Staff Name</th>
                <th className="px-4 py-4 text-left">Staff Type</th>
                <th className="px-4 py-4 text-left">Designation</th>
                <th className="px-4 py-4 text-left">Department</th>
                <th className="px-4 py-4 text-left">Phone</th>
                <th className="px-4 py-4 text-left">Login</th>
                <th className="px-4 py-4 text-left">Status</th>
                <th className="px-4 py-4 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="py-14 text-center">
                    <Loader2 className="mx-auto h-7 w-7 animate-spin text-blue-600" />
                    <p className="mt-3 text-sm text-gray-500">
                      Loading staff...
                    </p>
                  </td>
                </tr>
              ) : staffData.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-14 text-center">
                    <Users className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-3 font-semibold text-gray-700">
                      No staff members found
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Change the filters or add a staff member.
                    </p>
                  </td>
                </tr>
              ) : (
                staffData.map((staffMember) => (
                  <tr
                    key={staffMember.id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-4">
                      {staffMember.photo ? (
                        <img
                          src={staffMember.photo}
                          alt={staffMember.full_name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 font-semibold text-white">
                          {getInitials(staffMember.full_name)}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-4 text-gray-600">
                      {staffMember.employee_id || '—'}
                    </td>

                    <td className="px-4 py-4">
                      <p className="font-semibold text-gray-900">
                        {staffMember.full_name || '—'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {staffMember.email || '—'}
                      </p>
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`rounded-lg px-3 py-1 text-xs font-medium ${
                          staffMember.staff_type === 'TEACHING'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {staffMember.staff_type === 'TEACHING'
                          ? 'Teaching'
                          : 'Non-Teaching'}
                      </span>
                    </td>

                    <td className="px-4 py-4 text-gray-800">
                      {staffMember.designation || '—'}
                    </td>

                    <td className="px-4 py-4 text-gray-800">
                      {staffMember.department || '—'}
                    </td>

                    <td className="px-4 py-4 text-gray-800">
                      {staffMember.phone || '—'}
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`rounded px-3 py-1 text-xs font-medium ${
                          staffMember.login_status === 'ENABLED'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {staffMember.login_status === 'ENABLED'
                          ? 'Enabled'
                          : 'Disabled'}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          staffMember.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {staffMember.status === 'ACTIVE'
                          ? 'Active'
                          : 'Inactive'}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          title="View staff"
                          onClick={() =>
                            navigate(`/staff/view/${staffMember.id}`)
                          }
                          className="rounded-lg p-2 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        <button
                          type="button"
                          title="Edit staff"
                          onClick={() =>
                            navigate(`/staff/${staffMember.id}/edit`)
                          }
                          className="rounded-lg p-2 text-gray-400 hover:bg-orange-50 hover:text-orange-600"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>

                        <button
                          type="button"
                          title="Manage login access"
                          onClick={() => setSelectedStaff(staffMember)}
                          className="rounded-lg p-2 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                        >
                          {staffMember.login_status === 'ENABLED' ? (
                            <Lock className="h-4 w-4" />
                          ) : (
                            <Unlock className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-4 border-t border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500">
            Showing {startNumber} to {endNumber} of {pagination.count} staff members
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1 || loading}
              onClick={() => goToPage(pagination.page - 1)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>

            {pageNumbers.map((pageNumber, index) => {
              const previousNumber = pageNumbers[index - 1];

              return (
                <React.Fragment key={pageNumber}>
                  {previousNumber && pageNumber - previousNumber > 1 && (
                    <span className="px-1 text-gray-400">…</span>
                  )}

                  <button
                    type="button"
                    onClick={() => goToPage(pageNumber)}
                    disabled={loading}
                    className={`h-9 w-9 rounded-lg text-sm ${
                      pagination.page === pageNumber
                        ? 'bg-slate-900 text-white'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                </React.Fragment>
              );
            })}

            <button
              type="button"
              disabled={
                pagination.page >= pagination.total_pages || loading
              }
              onClick={() => goToPage(pagination.page + 1)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <StaffAccountAccessModal
        open={Boolean(selectedStaff)}
        schoolId={schoolId}
        staff={selectedStaff}
        onClose={() => setSelectedStaff(null)}
        onSaved={async (successMessage) => {
          setSelectedStaff(null);
          setToast({
            type: 'success',
            message: successMessage,
          });
          setRefreshVersion((current) => current + 1);
        }}
      />
    </>
  );
};

export default StaffTable;
